import React from 'react';

const styles: Record<string, React.CSSProperties> = {
  body: {
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
    lineHeight: 1.7,
    color: '#333',
    backgroundColor: '#f9f9f9',
    padding: 20,
    minHeight: '100vh'
  },
  container: {
    maxWidth: 800,
    margin: '0 auto',
    backgroundColor: '#fff',
    padding: 40,
    borderRadius: 8,
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  h1: {
    fontSize: 28,
    color: '#1a1a1a',
    marginBottom: 8,
    textAlign: 'center' as const
  },
  appName: {
    textAlign: 'center' as const,
    fontSize: 18,
    color: '#555',
    marginBottom: 4
  },
  lastUpdated: {
    textAlign: 'center' as const,
    color: '#666',
    fontSize: 14,
    marginBottom: 32
  },
  h2: {
    fontSize: 20,
    color: '#1a1a1a',
    marginTop: 32,
    marginBottom: 12,
    paddingBottom: 6,
    borderBottom: '2px solid #eee'
  },
  h3: {
    fontSize: 17,
    color: '#333',
    marginTop: 20,
    marginBottom: 8
  },
  p: {
    marginBottom: 12,
    textAlign: 'justify' as const
  },
  ul: {
    marginBottom: 12,
    paddingLeft: 24
  },
  li: {
    marginBottom: 6
  },
  highlight: {
    backgroundColor: '#f0f4ff',
    padding: 16,
    borderRadius: 6,
    borderLeft: '4px solid #4a6cf7',
    margin: '16px 0'
  },
  link: {
    color: '#4a6cf7',
    textDecoration: 'none'
  },
  footer: {
    marginTop: 40,
    paddingTop: 20,
    borderTop: '1px solid #eee',
    fontSize: 13,
    color: '#888',
    textAlign: 'center' as const
  }
};

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div style={styles.body}>
      <div style={styles.container}>
        <h1 style={styles.h1}>Privacy Policy</h1>
        <p style={styles.appName}>
          <strong>MegaValet</strong>
        </p>
        <p style={styles.lastUpdated}>Last updated: February 15, 2026</p>

        <div style={styles.highlight}>
          <p style={{ margin: 0 }}>
            <strong>Summary:</strong> MegaValet is a valet parking and car wash
            management application. We only collect the data necessary to
            operate the service, such as account information, vehicle
            photos/videos and push notifications. We do not sell your data to
            third parties.
          </p>
        </div>

        <h2 style={styles.h2}>1. Introduction</h2>
        <p style={styles.p}>
          MegaValet (&quot;we&quot;, &quot;our&quot;, or &quot;Company&quot;)
          operates the MegaValet mobile application (the &quot;App&quot;). This
          Privacy Policy describes how we collect, use, store, and protect your
          personal information when you use our App.
        </p>
        <p style={styles.p}>
          By creating an account and using the App, you agree to the collection
          and use of information in accordance with this policy. If you do not
          agree with any part of this policy, please do not use the App.
        </p>

        <h2 style={styles.h2}>2. Information We Collect</h2>

        <h3 style={styles.h3}>2.1 Account Information</h3>
        <p style={styles.p}>When you create a MegaValet account, we collect:</p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>First and last name</strong>
          </li>
          <li style={styles.li}>
            <strong>Email address</strong> (used as your login identifier)
          </li>
          <li style={styles.li}>
            <strong>Password</strong> (stored in encrypted form)
          </li>
          <li style={styles.li}>
            <strong>User type/role</strong> (Administrator, Agent, Valet,
            Washer)
          </li>
          <li style={styles.li}>
            <strong>Associated company(ies)</strong>
          </li>
        </ul>

        <h3 style={styles.h3}>2.2 Vehicle Operational Data</h3>
        <p style={styles.p}>
          In the course of valet and car wash operations, the App records:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            Vehicle identification (tag, make/model, color)
          </li>
          <li style={styles.li}>
            Vehicle status in the workflow (queued, washing, washed, delivered)
          </li>
          <li style={styles.li}>Timestamps for each service stage</li>
          <li style={styles.li}>Notes and observations about the vehicle</li>
          <li style={styles.li}>Vehicle checklist information</li>
          <li style={styles.li}>
            Record of which staff member serviced the vehicle
          </li>
        </ul>

        <h3 style={styles.h3}>2.3 Photos and Videos</h3>
        <p style={styles.p}>
          The App allows you to capture and store photos and videos of vehicles
          for documentation and checklist purposes. These media files are:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            Captured using the device camera or selected from the photo gallery
          </li>
          <li style={styles.li}>Uploaded and stored on secure cloud servers</li>
          <li style={styles.li}>
            Associated with the corresponding vehicle record
          </li>
        </ul>

        <h3 style={styles.h3}>2.4 Notification Data</h3>
        <p style={styles.p}>
          To send push notifications about vehicle status updates and
          operations, we collect:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Push notification token</strong> from your device (via Expo
            Push Notifications service)
          </li>
          <li style={styles.li}>
            Notification delivery records (title, message)
          </li>
        </ul>

        <h3 style={styles.h3}>2.5 Login and Security Information</h3>
        <p style={styles.p}>To protect your account, we record:</p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            Login attempts (for protection against unauthorized access)
          </li>
          <li style={styles.li}>Session authentication tokens</li>
        </ul>

        <h2 style={styles.h2}>3. Information We Do NOT Collect</h2>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>GPS Location:</strong> The App does not track your
            geographic location in real time.
          </li>
          <li style={styles.li}>
            <strong>Biometric Data:</strong> We do not collect fingerprints,
            facial recognition, or other biometric data.
          </li>
          <li style={styles.li}>
            <strong>Contacts:</strong> We do not access your contact list.
          </li>
          <li style={styles.li}>
            <strong>Browsing Data:</strong> We do not use third-party analytics
            or behavioral tracking tools.
          </li>
          <li style={styles.li}>
            <strong>Financial Data:</strong> We do not collect credit card
            information or banking details.
          </li>
        </ul>

        <h2 style={styles.h2}>4. How We Use Your Information</h2>
        <p style={styles.p}>
          We use your personal information for the following purposes:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Service operation:</strong> Managing the valet and car wash
            workflow
          </li>
          <li style={styles.li}>
            <strong>Authentication:</strong> Verifying your identity and
            providing secure access to your account
          </li>
          <li style={styles.li}>
            <strong>Notifications:</strong> Sending real-time updates about
            vehicle status and operations
          </li>
          <li style={styles.li}>
            <strong>Documentation:</strong> Recording vehicle condition through
            photos, videos, and notes
          </li>
          <li style={styles.li}>
            <strong>Auditing:</strong> Maintaining change records for quality
            control and dispute resolution
          </li>
          <li style={styles.li}>
            <strong>Account recovery:</strong> Sending verification codes via
            email for password reset
          </li>
          <li style={styles.li}>
            <strong>Service improvement:</strong> Analyzing aggregated
            operational data to optimize processes
          </li>
        </ul>

        <h2 style={styles.h2}>5. Device Permissions</h2>
        <p style={styles.p}>
          The App requests the following permissions on your device:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Camera:</strong> To capture photos and videos of vehicles
            for checklist and documentation
          </li>
          <li style={styles.li}>
            <strong>Photo Gallery:</strong> To select existing images and save
            new photos to the device
          </li>
          <li style={styles.li}>
            <strong>Push Notifications:</strong> To receive real-time alerts
            about vehicle status
          </li>
        </ul>
        <p style={styles.p}>
          All permissions are requested at the time of use and can be revoked at
          any time in your device settings.
        </p>

        <h2 style={styles.h2}>6. Data Storage and Security</h2>

        <h3 style={styles.h3}>6.1 Where Your Data Is Stored</h3>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Server:</strong> Account data, vehicle records, and
            operational information are stored in a secure database on cloud
            servers
          </li>
          <li style={styles.li}>
            <strong>Cloud Storage (AWS S3):</strong> Vehicle photos and videos
            are stored in Amazon Web Services&apos; secure cloud storage service
          </li>
          <li style={styles.li}>
            <strong>Local Device:</strong> Only the authentication token and the
            selected company identifier are stored locally in encrypted form
            (using the operating system&apos;s secure storage)
          </li>
        </ul>

        <h3 style={styles.h3}>6.2 Security Measures</h3>
        <p style={styles.p}>
          We adopt the following measures to protect your data:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>Passwords stored with encryption (hashing)</li>
          <li style={styles.li}>Token-based authentication with expiration</li>
          <li style={styles.li}>
            Brute-force attack protection (lockout after excessive login
            attempts)
          </li>
          <li style={styles.li}>Encrypted local storage on the device</li>
          <li style={styles.li}>Audit logging for change tracking</li>
          <li style={styles.li}>Communication via HTTPS</li>
        </ul>

        <h2 style={styles.h2}>7. Data Sharing</h2>
        <p style={styles.p}>
          <strong>
            We do not sell, rent, or share your personal information with third
            parties for marketing purposes.
          </strong>
        </p>
        <p style={styles.p}>
          Your data may be shared in the following limited circumstances:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Within your company:</strong> Other users in the same
            company may view vehicle operational data according to their access
            level
          </li>
          <li style={styles.li}>
            <strong>Infrastructure providers:</strong> We use third-party
            services exclusively to operate the App (see section 8)
          </li>
          <li style={styles.li}>
            <strong>Legal obligation:</strong> When required by law, court
            order, or competent regulatory authority
          </li>
        </ul>

        <h2 style={styles.h2}>8. Third-Party Services</h2>
        <p style={styles.p}>
          We use the following third-party services to operate the App:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Amazon Web Services (AWS):</strong> Server hosting, file
            storage (S3), and email delivery (SES)
          </li>
          <li style={styles.li}>
            <strong>Expo (expo.dev):</strong> Push notification service and app
            updates
          </li>
        </ul>
        <p style={styles.p}>
          These providers have their own privacy policies and are subject to
          their respective terms of service. We recommend reading these
          policies:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            AWS:{' '}
            <a
              href="https://aws.amazon.com/privacy/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.link}
            >
              https://aws.amazon.com/privacy/
            </a>
          </li>
          <li style={styles.li}>
            Expo:{' '}
            <a
              href="https://expo.dev/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.link}
            >
              https://expo.dev/privacy
            </a>
          </li>
        </ul>

        <h2 style={styles.h2}>9. Data Retention</h2>
        <p style={styles.p}>
          Your personal and operational data is retained while your account is
          active and as necessary to fulfill our legal and operational
          obligations.
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Account data:</strong> Retained while the account is active
          </li>
          <li style={styles.li}>
            <strong>Vehicle operational data:</strong> Retained according to the
            company&apos;s operational and legal needs
          </li>
          <li style={styles.li}>
            <strong>Photos and videos:</strong> Retained according to the
            company&apos;s operational needs
          </li>
          <li style={styles.li}>
            <strong>Audit records:</strong> Retained for compliance and
            traceability purposes
          </li>
        </ul>

        <h2 style={styles.h2}>10. Your Rights</h2>
        <p style={styles.p}>
          In accordance with the Brazilian General Data Protection Law (LGPD)
          and other applicable legislation, you have the following rights:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Access:</strong> Request information about what personal
            data we hold about you
          </li>
          <li style={styles.li}>
            <strong>Correction:</strong> Request correction of incorrect or
            outdated personal data
          </li>
          <li style={styles.li}>
            <strong>Deletion:</strong> Request deletion of your personal data,
            subject to legal retention obligations
          </li>
          <li style={styles.li}>
            <strong>Portability:</strong> Request transfer of your personal data
            in a readable format
          </li>
          <li style={styles.li}>
            <strong>Consent withdrawal:</strong> Withdraw your consent for data
            processing at any time
          </li>
          <li style={styles.li}>
            <strong>Objection:</strong> Object to the processing of personal
            data in certain circumstances
          </li>
        </ul>
        <p style={styles.p}>
          To exercise any of these rights, please contact us through the
          channels listed in section 14.
        </p>

        <h2 style={styles.h2}>11. Account Deletion</h2>
        <p style={styles.p}>
          You may request the deletion of your account at any time by contacting
          us. When your account is deleted:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            Your account will be deactivated and your login credentials will no
            longer be usable
          </li>
          <li style={styles.li}>
            Associated operational data may be retained in anonymized form for
            the company&apos;s historical and audit purposes
          </li>
          <li style={styles.li}>
            Photos and videos associated exclusively with your account will be
            removed from our servers
          </li>
        </ul>

        <h2 style={styles.h2}>12. Children&apos;s Privacy</h2>
        <p style={styles.p}>
          MegaValet is not intended for individuals under the age of 18. We do
          not knowingly collect data from minors. If we become aware that we
          have collected data from a minor, we will take steps to delete that
          information immediately.
        </p>

        <h2 style={styles.h2}>13. Changes to This Policy</h2>
        <p style={styles.p}>
          We may update this Privacy Policy from time to time. We will notify
          you of significant changes through an in-app notification or by email.
          The date of the last update will always be indicated at the top of
          this page.
        </p>
        <p style={styles.p}>
          We recommend that you review this policy periodically to stay informed
          about how we protect your data.
        </p>

        <h2 style={styles.h2}>14. Contact</h2>
        <p style={styles.p}>
          If you have any questions, concerns, or requests regarding this
          Privacy Policy or the processing of your personal data, please contact
          us:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            <strong>Email:</strong> contato@megavalet.com.br
          </li>
          <li style={styles.li}>
            <strong>App:</strong> Through the support section in the App
          </li>
        </ul>

        <div style={styles.footer}>
          <p>&copy; 2026 MegaValet. All rights reserved.</p>
          <p>
            This privacy policy was prepared in compliance with the Brazilian
            General Data Protection Law (LGPD - Law No. 13,709/2018) and the
            Google Play Store guidelines.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
